import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Layout from '../components/layout/Layout';
import AppointmentCard from '../components/appointments/AppointmentCard';
import DetailsModal from '../components/common/DetailsModal';
import FilterPanel from '../components/patients/FilterPanel';
import PaginationControls from '../components/patients/PaginationControls';
import DateRangePicker from '../components/common/DateRangePicker';
import { appointmentService } from '../services/appointmentService';
import { usePatients } from '../hooks/usePatients';

const AppointmentsPage = () => {
  const { loadBranches, branches } = usePatients();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [appointmentsPerPage] = useState(12);
  
  // Local filters for appointments page only
  const [localFilters, setLocalFilters] = useState({
    startDate: null,
    endDate: null,
    searchTerm: '',
    branchId: '',
    status: ''
  });

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchUnpaidAppointments = async (startDate, endDate) => {
    setLoading(true);
    try {
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);
      
      const data = await appointmentService.getUnpaidAppointments(fromDate, toDate);
      setAppointments(data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to fetch appointments');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
    // Initialize with default date range (last 7 days)
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 7);
    
    // Set local filters
    setLocalFilters(prev => ({
      ...prev,
      startDate: startDate,
      endDate: today
    }));
    
    // Fetch appointments with the default date range
    fetchUnpaidAppointments(startDate, today);
    // eslint-disable-next-line
  }, []);

  // Re-fetch when date filters change
  useEffect(() => {
    if (localFilters.startDate && localFilters.endDate) {
      fetchUnpaidAppointments(localFilters.startDate, localFilters.endDate);
    }
    // eslint-disable-next-line
  }, [localFilters.startDate, localFilters.endDate]);

  // Handle branch filter change - fetch appointments with branch filter
  const handleBranchFilterChange = (branchId) => {
    setLocalFilters(prev => ({ ...prev, branchId }));
    setCurrentPage(1);
    
    // If branch is selected, fetch appointments with branch filter
    if (branchId && localFilters.startDate && localFilters.endDate) {
      fetchUnpaidAppointmentsWithBranch(localFilters.startDate, localFilters.endDate, branchId);
    } else if (!branchId && localFilters.startDate && localFilters.endDate) {
      // If "All Branches" is selected, fetch all appointments without branch filter
      fetchUnpaidAppointments(localFilters.startDate, localFilters.endDate);
    }
  };

  const fetchUnpaidAppointmentsWithBranch = async (startDate, endDate, branchId) => {
    setLoading(true);
    try {
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);
      
      const data = await appointmentService.getUnpaidAppointments(fromDate, toDate, branchId);
      setAppointments(data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to fetch appointments');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  // Filter appointments based on all filter criteria
  const filteredAppointments = appointments.filter(apt => {
    // Search filter
    if (localFilters.searchTerm) {
      const search = localFilters.searchTerm.toLowerCase();
      const patientName = apt.profile?.name?.toLowerCase() || '';
      const sukraId = apt.sukra_id?.toLowerCase() || '';
      const orderId = apt.sukra_order_id?.toLowerCase() || '';
      const phone = apt.user?.phone?.toLowerCase() || '';
      
      const matchesSearch = patientName.includes(search) || 
                           sukraId.includes(search) || 
                           orderId.includes(search) ||
                           phone.includes(search);
      
      if (!matchesSearch) return false;
    }
    
    // Note: Branch filter is handled by API call, not client-side filtering
    // The API already filters by branch_id when selected
    
    // Report filter (status)
    if (localFilters.status) {
      if (localFilters.status === 'bloodReport') {
        // Show appointments with any blood tests (test codes starting with P)
        const hasBloodTest = apt.appointmentTests?.some(t => 
          t.availableTest?.test_code?.startsWith('P')
        );
        if (!hasBloodTest) return false;
      } else if (localFilters.status === 'dexaReport') {
        // Show appointments with DEXA test (code 003554)
        const hasDEXA = apt.appointmentTests?.some(t => 
          t.availableTest?.test_code === '003554'
        );
        if (!hasDEXA) return false;
      } else if (localFilters.status === 'vo2maxReport') {
        // Show appointments with VO2 MAX tests (codes 003555, 003556)
        const hasVO2 = apt.appointmentTests?.some(t => 
          t.availableTest?.test_code === '003555' || t.availableTest?.test_code === '003556'
        );
        if (!hasVO2) return false;
      }
    }
    
    return true;
  });
  
  // Pagination logic
  const indexOfLastAppointment = currentPage * appointmentsPerPage;
  const indexOfFirstAppointment = indexOfLastAppointment - appointmentsPerPage;
  const currentAppointments = filteredAppointments.slice(indexOfFirstAppointment, indexOfLastAppointment);
  const totalPages = Math.ceil(filteredAppointments.length / appointmentsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Memoize search handler to prevent re-renders
  const handleSearchChange = React.useCallback((value) => {
    setLocalFilters(prev => ({ ...prev, searchTerm: value }));
    setCurrentPage(1);
  }, []);

  // Get unique branches from appointments data, fallback to usePatients branches
  const appointmentBranches = Array.from(
    new Map(
      appointments
        .filter(apt => apt.facility?.id && apt.facility?.branch_name)
        .map(apt => [
          apt.facility.id, 
          { 
            id: apt.facility.id, 
            name: apt.facility.branch_name 
          }
        ])
    ).values()
  );

  const uniqueBranches = appointmentBranches.length > 0 
    ? appointmentBranches.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      })
    : (branches || []).map(branch => ({
        id: branch.id,
        name: branch.name || branch.branch_name
      })).sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

  // Create a custom FilterPanel that updates local filters instead of context
  const FilterPanelContent = React.memo(() => {
    return (
      <div className="bg-white rounded p-4 mb-4">
        {/* First Line: Title, Count, Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900">Appointments:</span>
            <span className="text-lg font-bold text-orange-500">{filteredAppointments.length}</span>
          </div>
          
          <input
            type="text"
            placeholder="Search appointments..."
            value={localFilters.searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded text-sm w-56"
          />

          {/* Date Presets - Right aligned */}
          <div className="flex gap-2 ml-auto">
            {[
              { key: 'lastMonth', label: 'Last Month' },
              { key: 'lastWeek', label: 'Last Week' },
              { key: 'today', label: 'Today' },
              { key: 'tomorrow', label: 'Tomorrow' }
            ].map((preset) => (
              <button
                key={preset.key}
                onClick={() => {
                  const today = new Date();
                  let startDate = null;
                  let endDate = null;

                  if (preset.key === 'today') {
                    startDate = new Date(today);
                    endDate = new Date(today);
                  } else if (preset.key === 'tomorrow') {
                    startDate = new Date(today);
                    startDate.setDate(startDate.getDate() + 1);
                    endDate = new Date(startDate);
                  } else if (preset.key === 'lastWeek') {
                    endDate = new Date(today);
                    startDate = new Date(today);
                    startDate.setDate(startDate.getDate() - 7);
                  } else if (preset.key === 'lastMonth') {
                    endDate = new Date(today);
                    startDate = new Date(today);
                    startDate.setMonth(startDate.getMonth() - 1);
                  }

                  setLocalFilters(prev => ({ ...prev, startDate, endDate }));
                }}
                className={`text-xs px-3 py-1.5 rounded font-semibold transition-colors ${
                  localFilters.startDate && 
                  localFilters.startDate.toDateString() === 
                  (preset.key === 'lastMonth' 
                    ? new Date(new Date().setMonth(new Date().getMonth() - 1)).toDateString()
                    : preset.key === 'lastWeek' 
                    ? new Date(new Date().setDate(new Date().getDate() - 7)).toDateString()
                    : preset.key === 'today'
                    ? new Date().toDateString()
                    : new Date(new Date().setDate(new Date().getDate() + 1)).toDateString())
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Second Line: Date Range, Branch, Report Filters, Reset */}
        <div className="flex items-center gap-3">
          {/* Date Range Picker */}
          <div style={{ marginBottom: '-15px' }}>
            <DateRangePicker
              startDate={localFilters.startDate}
              endDate={localFilters.endDate}
              onStartDateChange={(date) => {
                setLocalFilters(prev => ({ ...prev, startDate: date }));
              }}
              onEndDateChange={(date) => {
                setLocalFilters(prev => ({ ...prev, endDate: date }));
              }}
              className="flex gap-2"
            />
          </div>

          {/* Branch Filter */}
          <select
            value={String(localFilters.branchId)}
            onChange={(e) => {
              handleBranchFilterChange(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded text-sm h-10 min-w-48"
          >
            <option value="">All Branches</option>
            {uniqueBranches && uniqueBranches.length > 0 ? (
              uniqueBranches.map(branch => (
                <option key={branch.id} value={String(branch.id)}>
                  {(branch.name || '').replace('VITAL INSIGHTS-', '').trim() || `Branch ${branch.id}`}
                </option>
              ))
            ) : (
              <option disabled>No branches available</option>
            )}
          </select>

          {/* Report Filter */}
          <select
            value={localFilters.status}
            onChange={(e) => {
              setLocalFilters(prev => ({ ...prev, status: e.target.value }));
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded text-sm h-10 min-w-48"
          >
            <option value="">All Reports</option>
            <option value="bloodReport">Blood Report</option>
            <option value="dexaReport">DEXA Report</option>
            <option value="vo2maxReport">VO2 Max Report</option>
          </select>

          {/* Reset Button */}
          <button
            onClick={() => {
              const today = new Date();
              const startDate = new Date(today);
              startDate.setDate(startDate.getDate() - 7);
              setLocalFilters({
                startDate: startDate,
                endDate: today,
                searchTerm: '',
                branchId: '',
                status: ''
              });
              setCurrentPage(1);
            }}
            className="px-4 py-2 bg-gray-400 hover:bg-gray-600 text-white rounded text-sm font-semibold transition-colors h-10 ml-auto"
          >
            Reset
          </button>
        </div>
      </div>
    );
  });

  const AppointmentsFilterPanel = FilterPanelContent;

  return (
    <Layout>
      <AppointmentsFilterPanel />

      {/* Appointments Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading appointments...</div>
        </div>
      ) : filteredAppointments.length === 0 ? (
        <div className="flex items-center justify-center py-12 bg-white rounded">
          <div className="text-center">
            <p className="text-gray-500">No appointments found</p>
            <p className="text-gray-400 text-xs mt-1">Try adjusting your filters or date range</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
            {currentAppointments.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationControls 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Modal */}
      <DetailsModal
        data={selectedAppointment}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </Layout>
  );
};

export default AppointmentsPage;
